/**
 * Organizations — businesses in the Lead Command Center demo.
 *
 * List (name, industry, open leads, contacts, last activity, status) and a
 * detail pane at /demo/lcc/organizations/:orgId showing contacts, related
 * leads, and recent activity, with Edit Organization and shortcuts to add
 * work from the lead workflow.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2, Pencil, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { lccApi } from '@/lib/lcc-api';
import {
  formatLccDateTime,
  formatLccLabel,
  formatLccRelative,
  LccEmptyState,
  LccErrorCard,
  LccLoadingCard,
  LccPageHeader,
  LccStatusBadge,
} from './lcc-shared';
import { EditOrganizationDialog } from './record-dialogs';

export function LccOrganizationsView() {
  const { orgId } = useParams();
  if (orgId) return <OrganizationDetail orgId={orgId} />;
  return <OrganizationList />;
}

function OrganizationList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'organizations', search],
    queryFn: () => lccApi.organizations(search || undefined),
  });

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <LccPageHeader
        title="Organizations"
        blurb="The businesses behind your leads. Open one for its contacts, lead history, and activity."
      />
      <div className="relative max-w-[280px]">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search organizations…"
          className="flex h-9 w-full rounded-md border border-input bg-background pl-8 pr-2.5 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      {isLoading && <LccLoadingCard />}
      {error != null && <LccErrorCard title="Couldn't load organizations" error={error} />}
      {!isLoading && error == null && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">Organization</th>
                  <th className="px-4 py-2.5 font-semibold">Industry</th>
                  <th className="px-4 py-2.5 font-semibold">Open leads</th>
                  <th className="px-4 py-2.5 font-semibold">Contacts</th>
                  <th className="px-4 py-2.5 font-semibold">Last activity</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.items ?? []).map((org) => (
                  <tr
                    key={org.id}
                    className="cursor-pointer transition-colors hover:bg-muted/40"
                    onClick={() => navigate(`/demo/lcc/organizations/${org.id}`)}
                  >
                    <td className="px-4 py-2.5 font-medium">{org.name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{org.industry ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums">{org.openLeadCount}</td>
                    <td className="px-4 py-2.5 tabular-nums">{org.contactCount}</td>
                    <td className="px-4 py-2.5 text-muted-foreground" title={org.lastActivityAt}>
                      {formatLccRelative(org.lastActivityAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={org.status === 'active' ? 'success' : 'muted'}>
                        {formatLccLabel(org.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(data?.items ?? []).length === 0 && (
            <LccEmptyState icon={Building2} message="No organizations found." />
          )}
        </Card>
      )}
    </div>
  );
}

function OrganizationDetail({ orgId }: { orgId: string }) {
  const [editOpen, setEditOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'org-detail', orgId],
    queryFn: () => lccApi.organizationDetail(orgId),
  });

  if (isLoading) return <LccLoadingCard />;
  if (error != null || !data) {
    return <LccErrorCard title="Couldn't load this organization" error={error ?? new Error('Not found')} />;
  }

  const { organization, contacts, leads, recentEvents } = data;
  const openLeads = leads.filter((l) => !['converted', 'lost', 'spam', 'recycle_later'].includes(l.status));
  const previousLeads = leads.filter((l) => ['converted', 'lost', 'spam', 'recycle_later'].includes(l.status));

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <Link
        to="/demo/lcc/organizations"
        className="inline-flex items-center gap-1 text-[12px] text-muted-foreground underline-offset-2 hover:underline"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden />
        Organizations
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-[20px] font-semibold leading-tight">
            <Building2 className="h-5 w-5 text-primary" aria-hidden />
            {organization.name}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
            {organization.industry && <span>{organization.industry}</span>}
            {organization.website && (
              <a href={organization.website} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">
                {organization.website}
              </a>
            )}
            {organization.phone && <span>{organization.phone}</span>}
            {organization.email && <span>{organization.email}</span>}
            <Badge variant={organization.status === 'active' ? 'success' : 'muted'}>
              {formatLccLabel(organization.status)}
            </Badge>
          </div>
        </div>
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setEditOpen(true)}>
          <Pencil className="h-3.5 w-3.5" aria-hidden />
          Edit Organization
        </Button>
      </div>

      {organization.notes && (
        <Card className="p-3.5 text-[12.5px] leading-relaxed text-muted-foreground">{organization.notes}</Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="space-y-2.5 p-4">
          <h2 className="text-[13px] font-semibold">Contacts ({contacts.length})</h2>
          {contacts.length === 0 && (
            <p className="text-[12.5px] text-muted-foreground">No contacts on file.</p>
          )}
          <ul className="divide-y divide-border">
            {contacts.map((contact) => (
              <li key={contact.id} className="py-2 text-[12.5px]">
                <Link to={`/demo/lcc/contacts/${contact.id}`} className="font-medium underline-offset-2 hover:underline">
                  {contact.fullName}
                </Link>
                <div className="text-[11.5px] text-muted-foreground">
                  {[contact.roleTitle, contact.email, contact.phone].filter(Boolean).join(' · ') || '—'}
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="space-y-2.5 p-4">
          <h2 className="text-[13px] font-semibold">
            Leads ({openLeads.length} open{previousLeads.length > 0 ? `, ${previousLeads.length} previous` : ''})
          </h2>
          {leads.length === 0 && <p className="text-[12.5px] text-muted-foreground">No leads yet.</p>}
          <ul className="divide-y divide-border">
            {[...openLeads, ...previousLeads].map((lead) => (
              <li key={lead.id} className="flex flex-wrap items-center gap-2 py-2 text-[12.5px]">
                <Link to={`/demo/lcc/leads/${lead.id}`} className="min-w-0 flex-1 font-medium underline-offset-2 hover:underline">
                  {lead.title}
                </Link>
                <LccStatusBadge status={lead.status} label={lead.statusLabel} />
                <span className="text-[11.5px] text-muted-foreground">{formatLccRelative(lead.createdAt)}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="space-y-2.5 p-4">
        <h2 className="text-[13px] font-semibold">Recent activity</h2>
        {recentEvents.length === 0 && (
          <p className="text-[12.5px] text-muted-foreground">No activity recorded for this organization yet.</p>
        )}
        <ul className="space-y-1.5">
          {recentEvents.map((event) => (
            <li key={event.id} className="text-[12px] leading-snug">
              <Link to={`/demo/lcc/leads/${event.leadId}`} className="font-medium underline-offset-2 hover:underline">
                {formatLccLabel(event.eventType)}
              </Link>{' '}
              <span className="text-muted-foreground">
                {event.title} · {formatLccDateTime(event.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <EditOrganizationDialog organization={organization} open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
}
